import { Model, modelRegistry } from './Model';
import { KitchenTicketItem, Terminal, User } from '../models';
import { KitchenTicketType, KitchenTicketStatus, OrderFlagType, ProductType, PrintRoleType } from '../types';
import { apiService, connectionService } from '../services';
import { Store } from '../wrappers';
import { HashGen } from '../utilities';
import moment from 'moment-timezone';
export class KitchenTicket extends Model {
    static entity = 'kitchen_tickets';
    static primaryKey = 'hash';
    static syncable = true;
    id = null;
    hash = '';
    order_id = null;
    order_hash = '';
    order_ticket_id = null;
    order_ticket_hash = null;
    user_id = null;
    table_id = null;
    type = KitchenTicketType.dineIn;
    status = KitchenTicketStatus.pending;
    issued_at = null;
    last_activity_at = null;
    source = null;
    /** Callout code snapshot taken at creation (ADR-FN-006) — the same
     *  identifier printed on kitchen slips, receipts and hub cards. */
    callout_code = null;
    ack_at = null;
    created_at = null;
    updated_at = null;
    items = [];
    static fields() {
        return {
            id: this.attr(null),
            hash: this.attr(HashGen.long()),
            order_id: this.attr(null),
            order_hash: this.attr(''),
            order_ticket_id: this.attr(null),
            order_ticket_hash: this.attr(null),
            user_id: this.attr(null),
            table_id: this.attr(null),
            type: this.attr(KitchenTicketType.dineIn),
            status: this.attr(KitchenTicketStatus.pending),
            source: this.attr(null),
            callout_code: this.attr(null),
            issued_at: this.attr(null),
            last_activity_at: this.attr(null),
            ack_at: this.attr(null),
            created_at: this.attr(null),
            updated_at: this.attr(null),
            items: this.attr([], (value) => (value || []).map(item => new KitchenTicketItem(item))),
        };
    }
    constructor(record) {
        super();
        this.$fill(record);
    }
    static async fetchLatest(initial = false) {
        if (!Store.get('isOnline'))
            return;
        let params = {};
        if (!initial) {
            const lastUpdatedAt = KitchenTicket.getLastUpdatedAt();
            params = { last_updated_at: lastUpdatedAt ? lastUpdatedAt.utc().format('YYYY-MM-DD HH:mm:ss') : null };
        }
        const response = await apiService.get('/kitchen-tickets/latest', { params: params });
        if (initial) {
            let kitchenTickets = KitchenTicket.all().filter(kt => !kt.id);
            KitchenTicket.create({ data: kitchenTickets.map(item => item.$toJson()) });
        }
        KitchenTicket.insert({ data: response.data.records });
    }
    static getLastUpdatedAt() {
        return KitchenTicket.all().length
            ? moment(Math.max(...KitchenTicket.all().filter(kt => kt.updated_at).map(kt => moment(kt.updated_at).valueOf())))
            : null;
    }
    getIssuedAt() {
        if (!this.issued_at) {
            return null;
        }
        return moment.utc(this.issued_at).local();
    }
    static createNew(order) {
        const kitchenOrderItems = order.items.filter(orderItem => {
            const product = orderItem.getProduct();
            return product?.type === ProductType.food || product?.printing_sector === PrintRoleType.kitchen;
        }).map(orderItem => orderItem.$duplicate());
        if (!kitchenOrderItems.length)
            return undefined;
        const kitchenTicket = new KitchenTicket({
            order_id: order.id,
            order_hash: order.hash,
            user_id: order.user_id,
            table_id: order.table_id,
            type: order.source && order.source !== 'qr' ? KitchenTicketType.delivery : KitchenTicketType.dineIn,
            status: KitchenTicketStatus.pending,
            source: order.source,
            callout_code: order.getFlag(OrderFlagType.calloutCode)?.[0] || null,
            issued_at: moment().utc().format(KitchenTicket.DATE_FORMAT),
            last_activity_at: moment().utc().format(KitchenTicket.DATE_FORMAT),
            ack_at: null,
        });
        kitchenTicket.items = kitchenOrderItems.map((orderItem) => {
            return new KitchenTicketItem({
                kitchen_ticket_id: kitchenTicket.id,
                kitchen_ticket_hash: kitchenTicket.hash,
                order_item_id: orderItem.id,
                order_item_hash: orderItem.hash,
                product_id: orderItem.product_id,
                name: orderItem.name,
                count: orderItem.count,
                comments: orderItem.comments,
            });
        });
        return kitchenTicket;
    }
    /**
     * Create a KitchenTicket from an OrderTicket (no Order required).
     * Used during the acceptance phase when the order has not been created yet.
     */
    static createFromOrderTicket(ticket) {
        const modules = Store.get('modules');
        const kdsRegistered = Terminal.all().some(t => t.isKitchenDisplaySystem());
        if (!modules?.kds || !kdsRegistered)
            return undefined;
        const kitchenItems = [];
        for (const orderItem of ticket.items) {
            const product = orderItem.getMappedProduct();
            if (!product || !product.id)
                continue;
            if (product.type !== ProductType.food && product.printing_sector !== PrintRoleType.kitchen)
                continue;
            kitchenItems.push({
                product_id: product.id,
                name: product.name,
                count: String(orderItem.quantity),
                comments: [...orderItem.attributes],
            });
        }
        if (!kitchenItems.length)
            return undefined;
        const kitchenTicket = new KitchenTicket({
            order_ticket_id: ticket.id,
            order_ticket_hash: ticket.hash,
            order_id: null,
            order_hash: '',
            // Manual tickets are typed in and fired by the logged-in waiter on
            // this terminal — attribute them like dine-in tickets. API tickets
            // have no human author, so they stay unattributed.
            user_id: ticket.isManual() ? (User.current()?.id ?? null) : null,
            table_id: null,
            type: KitchenTicketType.delivery,
            status: KitchenTicketStatus.pending,
            source: ticket.source,
            callout_code: ticket.callout_code,
            issued_at: moment().utc().format(KitchenTicket.DATE_FORMAT),
            last_activity_at: moment().utc().format(KitchenTicket.DATE_FORMAT),
            ack_at: null,
        });
        kitchenTicket.items = kitchenItems.map(item => new KitchenTicketItem({
            kitchen_ticket_id: kitchenTicket.id,
            kitchen_ticket_hash: kitchenTicket.hash,
            order_item_id: null,
            order_item_hash: null,
            product_id: item.product_id,
            name: item.name,
            count: item.count,
            comments: item.comments,
        }));
        return kitchenTicket;
    }
    /**
     * Create and sync a kitchen ticket from an order ticket.
     * Mirrors KitchenTicket.issue(order) but for the deferred-fiscalization flow.
     */
    static async issueFromOrderTicket(ticket) {
        const kitchenTicket = KitchenTicket.createFromOrderTicket(ticket);
        if (!kitchenTicket)
            return { success: false };
        return kitchenTicket.sync('create');
    }
    static async issue(order) {
        const modules = Store.get('modules');
        const kdsRegistered = Terminal.all().some(t => t.isKitchenDisplaySystem());
        if (!modules?.kds || !kdsRegistered)
            return { success: false };
        const kitchenTicket = KitchenTicket.createNew(order);
        if (!kitchenTicket)
            return { success: false };
        return kitchenTicket.sync('create');
    }
    static async cancel(order) {
        const modules = Store.get('modules');
        const kdsRegistered = Terminal.all().some(t => t.isKitchenDisplaySystem());
        if (!modules?.kds || !kdsRegistered)
            return { success: false };
        const kitchenTicket = KitchenTicket.all().find(kt => kt.order_hash === order.hash);
        if (!kitchenTicket)
            return { success: false };
        kitchenTicket.status = KitchenTicketStatus.canceled;
        return kitchenTicket.sync('update');
    }
    /**
     * Cancel a kitchen ticket by order ticket reference.
     * Used for pre-completion cancellations where no Order exists.
     */
    static async cancelByOrderTicket(ticket) {
        const modules = Store.get('modules');
        if (!modules?.kds)
            return { success: false };
        const kitchenTicket = KitchenTicket.all().find(kt => kt.order_ticket_hash === ticket.hash);
        if (!kitchenTicket)
            return { success: false };
        kitchenTicket.status = KitchenTicketStatus.canceled;
        return kitchenTicket.sync('update');
    }
    static async deleteItem(order, orderItem) {
        const modules = Store.get('modules');
        const kdsRegistered = Terminal.all().some(t => t.isKitchenDisplaySystem());
        if (!modules?.kds || !kdsRegistered)
            return { success: false };
        const kitchenTicket = KitchenTicket.all().find(kt => kt.order_hash === order.hash);
        if (!kitchenTicket)
            return { success: false };
        kitchenTicket.items = kitchenTicket.items.filter(i => i.order_item_hash !== orderItem.hash);
        return kitchenTicket.sync(!kitchenTicket.items.length ? 'delete' : 'update');
    }
    static async updateItemCount(order, orderItem) {
        const modules = Store.get('modules');
        const kdsRegistered = Terminal.all().some(t => t.isKitchenDisplaySystem());
        if (!modules?.kds || !kdsRegistered)
            return { success: false };
        const kitchenTicket = KitchenTicket.all().find(kt => kt.order_hash === order.hash);
        if (!kitchenTicket)
            return { success: false };
        const kitchenTicketItem = kitchenTicket.items.find(i => i.order_item_hash === orderItem.hash);
        if (!kitchenTicketItem)
            return { success: false };
        kitchenTicketItem.count = orderItem.count;
        return kitchenTicket.sync('update');
    }
    static async replaceOrder(originalOrder, newOrder) {
        const modules = Store.get('modules');
        const kdsRegistered = Terminal.all().some(t => t.isKitchenDisplaySystem());
        if (!modules?.kds || !kdsRegistered)
            return { success: false };
        const kitchenTicket = KitchenTicket.all().find(kt => kt.order_hash === originalOrder.hash);
        if (!kitchenTicket)
            return { success: false };
        const newKitchenTicket = KitchenTicket.createNew(newOrder);
        if (!newKitchenTicket)
            return { success: false };
        kitchenTicket.order_id = newKitchenTicket.order_id;
        kitchenTicket.order_hash = newKitchenTicket.order_hash;
        kitchenTicket.items = newKitchenTicket.items.map(item => {
            item.kitchen_ticket_id = kitchenTicket.id;
            item.kitchen_ticket_hash = kitchenTicket.hash;
            return item;
        });
        return kitchenTicket.sync('update');
    }
    async complete() {
        this.last_activity_at = moment().utc().format(KitchenTicket.DATE_FORMAT);
        this.status = KitchenTicketStatus.done;
        return this.sync('update');
    }
    async recall() {
        this.last_activity_at = moment().utc().format(KitchenTicket.DATE_FORMAT);
        this.status = KitchenTicketStatus.pending;
        this.ack_at = null;
        return this.sync('update');
    }
    async acknowledge() {
        this.ack_at = moment().utc().format(KitchenTicket.DATE_FORMAT);
        return this.sync('update');
    }
    async completeItem(item, complete = true, sync = true) {
        const kitchenItem = this.items.find((i) => i.hash === item.hash);
        if (!kitchenItem)
            return { success: false };
        kitchenItem.complete(complete);
        this.last_activity_at = moment().utc().format(KitchenTicket.DATE_FORMAT);
        if (sync) {
            return this.sync('update');
        }
        return { success: true };
    }
    async transferToTable(table) {
        this.table_id = table.id;
        return this.sync('update');
    }
    async transferToUser(user) {
        this.user_id = user.id;
        return this.sync('update');
    }
    async sync(action) {
        const apiAction = action === 'delete' ? 'delete' : (!this.id ? 'post' : 'patch');
        const url = '/kitchen-tickets' + (this.id ? `/${this.id}` : '');
        let record = this.$toJson();
        await this.markForSync(action);
        if (action === 'delete')
            this.$delete();
        else
            this.$save();
        if (!Store.get('isOnline')) {
            KitchenTicket.syncTerminals({ items: [{ action: apiAction, record }] });
            return { success: true };
        }
        try {
            const response = await apiService[apiAction](url, record);
            if (response.data.record) {
                record = response.data.record;
                this.$fill(record);
                KitchenTicket.insert({ data: [record] });
            }
            KitchenTicket.syncTerminals({ items: [{ action: apiAction, record }] });
            await this.removeFromSync();
            return { success: true };
        }
        catch (error) {
            const isOnline = await connectionService.connectionCheckRetry(5000);
            return { success: isOnline };
        }
    }
    isPending() {
        return this.status === KitchenTicketStatus.pending;
    }
    isDone() {
        return this.status === KitchenTicketStatus.done;
    }
    isCanceled() {
        return this.status === KitchenTicketStatus.canceled;
    }
    isAcknowledged() {
        return !!this.ack_at;
    }
}
export default KitchenTicket;
modelRegistry.register('KitchenTicket', KitchenTicket);
//# sourceMappingURL=KitchenTicket.js.map